'use client';

import type { SubscriptionPlan } from '../interfaces/subscriptions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles } from 'lucide-react';

const planCurrencyFormatters = new Map<string, Intl.NumberFormat>();
function getPlanCurrencyFormatter(currency: string) {
  if (!planCurrencyFormatters.has(currency)) {
    planCurrencyFormatters.set(
      currency,
      new Intl.NumberFormat('es-CL', { style: 'currency', currency }),
    );
  }
  return planCurrencyFormatters.get(currency)!;
}

function getFrequencyText(plan: SubscriptionPlan) {
  const freq = plan.frequency === 1 ? '' : plan.frequency;
  const type = {
    days: 'días',
    months: plan.frequency === 1 ? 'mes' : 'meses',
    years: plan.frequency === 1 ? 'año' : 'años',
  }[plan.frequency_type];
  return `${freq} ${type}`.trim();
}

interface PublicPlanCardProps {
  plan: SubscriptionPlan;
  featured?: boolean;
  index?: number;
  onSelect: (plan: SubscriptionPlan) => void;
  disabled?: boolean;
}

export function PublicPlanCard({
  plan,
  featured,
  index = 0,
  onSelect,
  disabled,
}: PublicPlanCardProps) {
  const formatPrice = () =>
    getPlanCurrencyFormatter(plan.currency).format(Number(plan.price) || 0);
  const features = plan.features ?? [];

  return (
    <article
      className={`
        group relative flex flex-col rounded-3xl border transition-all duration-500
        animate-in fade-in slide-in-from-bottom-4 fill-mode-both
        ${featured
          ? 'border-[color-mix(in_srgb,var(--subs-accent)_45%,transparent)] shadow-2xl shadow-[color-mix(in_srgb,var(--subs-accent)_15%,transparent)] scale-[1.02] lg:scale-105 z-10'
          : 'border-white/8 bg-card/30 hover:border-[color-mix(in_srgb,var(--subs-accent)_25%,transparent)] hover:bg-card/50'
        }
      `}
      style={{
        animationDelay: `${index * 80}ms`,
        ...(featured
          ? {
              background: `linear-gradient(165deg, color-mix(in srgb, var(--subs-accent) 14%, transparent) 0%, hsl(var(--card)/0.55) 45%, hsl(var(--card)/0.35) 100%)`,
            }
          : {}),
      }}
    >
      {featured && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <Badge
            className="gap-1 border-0 px-3 py-1 text-xs font-semibold text-white shadow-lg"
            style={{ background: 'var(--subs-accent)' }}
          >
            <Sparkles className="h-3 w-3" />
            Recomendado
          </Badge>
        </div>
      )}

      <div className="flex flex-1 flex-col p-6 sm:p-8">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Plan
          </p>
          <h3 className="font-[family-name:var(--font-outfit)] text-2xl font-bold tracking-tight sm:text-3xl">
            {plan.name}
          </h3>
          {plan.description && (
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">
              {plan.description}
            </p>
          )}
        </header>

        <div className="mb-6">
          <div className="flex items-baseline gap-1">
            <span
              className="font-[family-name:var(--font-outfit)] text-4xl font-bold tracking-tight sm:text-5xl"
              style={{ color: featured ? 'var(--subs-accent)' : undefined }}
            >
              {formatPrice()}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">por {getFrequencyText(plan)}</p>
        </div>

        {plan.trial_days > 0 && (
          <div
            className="mb-6 rounded-xl border px-3 py-2.5 text-center text-xs font-medium"
            style={{
              borderColor: 'color-mix(in srgb, var(--subs-accent) 30%, transparent)',
              background: 'color-mix(in srgb, var(--subs-accent) 8%, transparent)',
              color: 'var(--subs-accent)',
            }}
          >
            {plan.trial_days} días de prueba incluidos
          </div>
        )}

        {features.length > 0 && (
          <ul className="mb-8 flex-1 space-y-3">
            {features.map((feature) => (
              <li key={feature} className="flex items-start gap-3 text-sm">
                <span
                  className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                  style={{
                    background: 'color-mix(in srgb, var(--subs-accent) 15%, transparent)',
                  }}
                >
                  <Check className="h-3 w-3 text-[var(--subs-accent)]" />
                </span>
                <span className="text-foreground/90 leading-snug">{feature}</span>
              </li>
            ))}
          </ul>
        )}

        <Button
          type="button"
          disabled={disabled}
          onClick={() => onSelect(plan)}
          className={`
            h-12 w-full rounded-xl font-semibold transition-all duration-300
            ${featured
              ? 'text-white shadow-lg hover:opacity-90'
              : 'border border-[color-mix(in_srgb,var(--subs-accent)_30%,transparent)] bg-transparent text-foreground hover:bg-[color-mix(in_srgb,var(--subs-accent)_10%,transparent)]'
            }
          `}
          style={featured ? { background: 'var(--subs-accent)' } : undefined}
          variant={featured ? 'default' : 'outline'}
        >
          Elegir este plan
        </Button>
      </div>
    </article>
  );
}
